///////////////////////////////////////////////////

import { apiConnector } from 'apiConnector/secondary-market/legacyApiConnector';
import { ofType } from 'redux-observable';
import { of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { showNotificationThunk } from 'state/notifications/notification.thunk';
import { secondaryMarketRtFormSelectDefaultClientFromProductThunk } from 'state/secondaryMarket/realtimeQuote';
import {
  secondaryMarketAstroProductFetched,
  secondaryMarketAstroProductFetchFailed,
  secondaryMarketAstroProductFetchProductNotFound,
} from 'state/secondaryMarket/referential/astroProduct/astroProduct.actions';
import {
  type AstroProduct,
  mapAstroProductClientsToISalesCounterpart,
} from 'state/secondaryMarket/referential/astroProduct/astroProduct.model';
import type { EpicWithThunk } from 'state/state.common';
import { secondaryRealtimeQuoteTabCreatedThunk } from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.thunks';
import type { HttpError } from 'utils/http/httpClient.model';

export function loadAstroProductEpic(): EpicWithThunk {
  return (actions$, _state$, { httpClient }) => {
    const { loadAstroProduct } = apiConnector(httpClient);

    return actions$.pipe(
      ofType('SECONDARY_MARKET_ASTRO_PRODUCT_FETCH_REQUESTED'),
      mergeMap(({ isin, tabId, createTabOnAstroProductFetched }) => {
        return loadAstroProduct(isin).pipe(
          mergeMap(data => {
            if (data === 'not_found') {
              const notFound = secondaryMarketAstroProductFetchProductNotFound(isin);
              if (createTabOnAstroProductFetched) {
                const showNotification = showNotificationThunk({
                  level: 'danger',
                  title: { id: 'secondaryMarket.errors.productNotFound.external.title' },
                  content: [{ id: 'secondaryMarket.errors.technical.external', values: { isin } }],
                  closeAutomatically: true,
                });
                return of(notFound, showNotification);
              }
              return of(notFound);
            }
            const productFetched = secondaryMarketAstroProductFetched(isin, data as AstroProduct);
            if (tabId) {
              return of(
                productFetched,
                secondaryMarketRtFormSelectDefaultClientFromProductThunk(
                  tabId,
                  mapAstroProductClientsToISalesCounterpart(data.clients),
                ),
              );
            }

            if (createTabOnAstroProductFetched && !!data.isin) {
              return of(productFetched, secondaryRealtimeQuoteTabCreatedThunk(data.isin));
            }
            return of(productFetched);
          }),
          catchError((err: HttpError) =>
            of(
              secondaryMarketAstroProductFetchFailed(isin, err.message),
              showNotificationThunk({
                level: 'danger',
                closeAutomatically: true,
                title: { id: 'toast.unexpectedError' },
                content: [{ id: 'secondaryMarket.errors.technical.astroLoading', values: { isin } }],
              }),
            ),
          ),
        );
      }),
    );
  };
}
